import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList } from "recharts";
import { Trophy, Award } from "lucide-react";
import { fmtNum, ctrTier } from "./pmaxUtils";

export const BenchmarkBadge = ({ ctr }) => {
  const label = ctrTier(ctr);
  if (!label) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 mb-5" data-testid="pmax-benchmark-badge">
      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-lime-400/15 border border-lime-400/40 text-lime-200 text-sm font-semibold">
        <Award className="w-4 h-4 text-lime-300" /> {label}
      </span>
      <span className="text-xs text-slate-300">
        {ctr.toFixed(2)}% click rate. Most Performance Max accounts land between 0.83% and 2.30%.
      </span>
    </div>
  );
};

const pickBestDay = (daily, byImp) =>
  daily.filter((d) => !d.catchup).reduce((best, d) => (!best || (byImp ? d.impressions > best.impressions : d.clicks > best.clicks || (d.clicks === best.clicks && d.impressions > best.impressions)) ? d : best), null);

// Catch-up days/weeks: impressions dumped at once. Shown capped to the normal scale.
const markCatchups = (daily, weekly) => {
  const imps = daily.map((d) => d.impressions || 0).filter((v) => v > 0).sort((a, b) => a - b);
  if (imps.length < (weekly ? 3 : 5)) return { rows: daily, capI: null, capC: null };
  const median = imps[Math.floor(imps.length / 2)];
  const isCatch = (d) => (d.impressions || 0) > median * (weekly ? 2 : 3);
  const normal = daily.filter((d) => !isCatch(d));
  if (normal.length === daily.length) return { rows: daily, capI: null, capC: null };
  const nice = (v) => { const s = 5 * 10 ** Math.max(0, Math.floor(Math.log10(v)) - 1); return Math.ceil(v / s) * s; };
  const capI = nice(Math.max(...normal.map((d) => d.impressions || 0)) * 1.15);
  const capC = Math.ceil(Math.max(1, ...normal.map((d) => d.clicks || 0)) * 1.15);
  const rows = daily.map((d) => (isCatch(d)
    ? { ...d, catchup: true, impShown: capI, clkShown: Math.min(d.clicks || 0, capC) }
    : { ...d, impShown: d.impressions, clkShown: d.clicks }));
  return { rows, capI, capC };
};

const ChartTip = ({ active, payload, label, unit, hideClicks }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#062a3a] border border-cyan-300/30 rounded-lg px-3 py-2 text-xs max-w-[240px]">
      <p className="text-white font-semibold mb-1">{row.tipLabel || label}</p>
      <p className="text-cyan-200">{fmtNum(row.impressions)} impressions</p>
      {!hideClicks && <p className="text-lime-300">{fmtNum(row.clicks)} clicks</p>}
      {row.avg7 != null && <p className="text-white/80">{fmtNum(row.avg7)} impressions, 7-day average</p>}
      {row.catchup && <p className="text-slate-300 mt-1 whitespace-normal">Catch-up {unit}: the platform delivered leftover budget all at once, so this bar is shortened to keep the other {unit}s readable.</p>}
    </div>
  );
};

const CatchupTag = ({ x, y, width, index, rows }) => {
  const r = rows[index];
  if (!r?.catchup || width < 28) return null;
  return (
    <g>
      <text x={x + width / 2} y={y + 12} textAnchor="middle" fill="#cffafe" fontSize={9} fontWeight={700}>{fmtNum(r.impressions)}</text>
      <text x={x + width / 2} y={y - 4} textAnchor="middle" fill="#e2e8f0" fontSize={9} fontWeight={600}>Catch-up</text>
    </g>
  );
};

// Trailing 7-day average of impressions (by calendar date when `iso` is present, else by the last 7 points)
const withAvg7 = (daily) => daily.map((d, i) => {
  const win = (d.iso
    ? daily.filter((x, j) => j <= i && (new Date(d.iso) - new Date(x.iso)) / 86400000 < 7)
    : daily.slice(Math.max(0, i - 6), i + 1)).filter((x) => !x.catchup);
  return { ...d, avg7: win.length ? Math.round(win.reduce((s, x) => s + (x.impressions || 0), 0) / win.length) : null };
});

export const DailyChart = ({ daily = [], avg7 = false, capSpikes = false, unit = "day", hideClicks = false }) => {
  const { rows: marked, capI, capC } = capSpikes ? markCatchups(daily, unit === "week") : { rows: daily, capI: null, capC: null };
  const best = pickBestDay(marked, hideClicks);
  const data = avg7 ? withAvg7(marked) : marked;
  const capped = capI != null;
  return (
    <div data-testid="pmax-daily-chart">
      {best && (
        <p className="text-slate-300 text-sm mb-3" data-testid="pmax-best-day">
          <Trophy className="inline w-4 h-4 text-lime-300 mr-1 -mt-0.5" />
          Best {unit}: <span className="text-white font-semibold">{best.bestLabel || best.date}</span>, with {hideClicks ? `${fmtNum(best.impressions)} impressions` : `${fmtNum(best.clicks)} clicks from ${fmtNum(best.impressions)} impressions`}.
        </p>
      )}
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: capped ? 18 : 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} interval="preserveStartEnd" minTickGap={12} />
            <YAxis yAxisId="i" tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 100) / 10}K` : v)} domain={capped ? [0, capI] : [0, "auto"]} allowDataOverflow={capped} />
            {!hideClicks && <YAxis yAxisId="c" orientation="right" tick={{ fill: "#bef264", fontSize: 10 }} domain={capped ? [0, capC] : [0, "auto"]} allowDataOverflow={capped} />}
            <Tooltip content={<ChartTip unit={unit} hideClicks={hideClicks} />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Bar yAxisId="i" dataKey={capped ? "impShown" : "impressions"} fill="#22d3ee" fillOpacity={0.55} radius={[3, 3, 0, 0]}>
              {capped && data.map((d) => <Cell key={d.date} fill={d.catchup ? "#0e7490" : "#22d3ee"} fillOpacity={d.catchup ? 0.85 : 0.55} />)}
              {capped && <LabelList dataKey="impShown" content={(p) => <CatchupTag {...p} rows={data} />} />}
            </Bar>
            {!hideClicks && <Line yAxisId="c" dataKey={capped ? "clkShown" : "clicks"} stroke="#bef264" strokeWidth={2} dot={unit === "week"} type="monotone" />}
            {avg7 && <Line yAxisId="i" dataKey="avg7" stroke="#ffffff" strokeWidth={2.5} dot={false} type="monotone" strokeOpacity={0.9} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-4 text-[11px] text-slate-400 mt-2">
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-cyan-400/60 mr-1" />Impressions</span>
        {!hideClicks && <span><span className="inline-block w-2.5 h-0.5 bg-lime-300 mr-1 align-middle" />Clicks</span>}
        {avg7 && <span data-testid="daily-avg7-key"><span className="inline-block w-3 h-0.5 bg-white mr-1 align-middle" />7-day average impressions</span>}
        {capped && <span data-testid="daily-catchup-key"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-cyan-700/90 mr-1" />Catch-up {unit} (leftover budget delivered at once, bar shortened)</span>}
      </div>
    </div>
  );
};
